import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  GraduationCap, Plus, CheckCircle2, ArrowLeft, Save,
  Trash2, Edit, Users, Loader2, ChevronRight, Award, X, Brain
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrainingTracker() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [myProgress, setMyProgress] = useState(null);
  const [allProgress, setAllProgress] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [saving, setSaving] = useState(false);
  const isAdmin = ['owner', 'area_manager'].includes(user?.role);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', items: [''] });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => { fetchChecklists(); }, []);

  const fetchChecklists = async () => {
    try { setLoading(true); const res = await axios.get(`${API_URL}/training-checklists`); setChecklists(res.data); }
    catch { toast.error('Failed to load training checklists'); }
    finally { setLoading(false); }
  };

  const openChecklist = async (cl) => {
    setSelectedChecklist(cl);
    try { const res = await axios.get(`${API_URL}/training-checklists/${cl.id}/my-progress`); setMyProgress(res.data); setViewMode('do'); }
    catch { toast.error('Failed to load progress'); }
  };

  const toggleItem = (itemId) => {
    if (!myProgress) return;
    const checked = myProgress.checked_items || [];
    setMyProgress({ ...myProgress, checked_items: checked.includes(itemId) ? checked.filter(i => i !== itemId) : [...checked, itemId] });
  };

  const saveProgress = async (markComplete = false) => {
    if (!selectedChecklist || !myProgress) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/training-checklists/${selectedChecklist.id}/my-progress`, { checked_items: myProgress.checked_items || [], completed: markComplete });
      if (markComplete) { setMyProgress({ ...myProgress, completed: true }); toast.success('Training checklist completed!'); }
      else toast.success('Progress saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const viewAdminProgress = async (cl) => {
    setSelectedChecklist(cl);
    try { const res = await axios.get(`${API_URL}/training-checklists/${cl.id}/all-progress`); setAllProgress(res.data); setViewMode('admin-progress'); }
    catch { toast.error('Failed to load'); }
  };

  const openCreate = () => { setEditingId(null); setEditForm({ title: '', items: [''] }); setEditOpen(true); };
  const openEdit = (cl) => { setEditingId(cl.id); setEditForm({ title: cl.title, items: cl.items.map(i => i.text) }); setEditOpen(true); };
  const addItem = () => setEditForm({ ...editForm, items: [...editForm.items, ''] });
  const removeItem = (idx) => setEditForm({ ...editForm, items: editForm.items.filter((_, i) => i !== idx) });
  const updateItem = (idx, val) => { const items = [...editForm.items]; items[idx] = val; setEditForm({ ...editForm, items }); };

  const handleSaveChecklist = async () => {
    const valid = editForm.items.filter(i => i.trim());
    if (!editForm.title.trim() || valid.length === 0) { toast.error('Title and at least one item required'); return; }
    try {
      const body = { title: editForm.title, items: valid.map(t => ({ text: t })) };
      if (editingId) await axios.put(`${API_URL}/training-checklists/${editingId}`, body);
      else await axios.post(`${API_URL}/training-checklists`, body);
      toast.success(editingId ? 'Updated' : 'Created');
      setEditOpen(false); fetchChecklists();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this training checklist?')) return;
    try { await axios.delete(`${API_URL}/training-checklists/${id}`); toast.success('Deleted'); fetchChecklists(); }
    catch { toast.error('Failed to delete'); }
  };

  const goBack = () => { setViewMode('list'); setSelectedChecklist(null); setMyProgress(null); setAllProgress([]); };

  // === Doing checklist ===
  if (viewMode === 'do' && selectedChecklist) {
    const items = selectedChecklist.items || [];
    const checked = myProgress?.checked_items || [];
    const pct = items.length > 0 ? Math.round((checked.length / items.length) * 100) : 0;
    const allDone = checked.length === items.length && items.length > 0;

    return (
      <div className="space-y-5 animate-fade-in" data-testid="training-do-view">
        <button onClick={goBack} className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-600 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Training
        </button>

        <Card className="border-0 bg-gradient-to-br from-indigo-500 to-violet-600 text-white rounded-2xl shadow-lg shadow-indigo-200/40 overflow-hidden">
          <CardContent className="p-5 sm:p-6">
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>{selectedChecklist.title}</h1>
            <div className="mt-4 flex items-center gap-3">
              <div className="flex-1 h-2.5 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-white rounded-full progress-animate" style={{ width: `${pct}%` }} />
              </div>
              <span className="text-lg font-bold tabular-nums">{pct}%</span>
            </div>
            <p className="text-indigo-200 mt-2 text-sm">{checked.length} of {items.length} completed</p>
            {myProgress?.completed && (
              <div className="mt-3 bg-white/15 rounded-xl px-4 py-2.5 flex items-center gap-2 backdrop-blur-sm">
                <Award className="w-5 h-5 text-amber-300" />
                <span className="text-white/90 font-medium text-sm">Completed</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-slate-200/80 rounded-2xl overflow-hidden">
          <CardContent className="p-0 divide-y divide-slate-100">
            {items.map((item, idx) => {
              const isChecked = checked.includes(item.id);
              return (
                <label
                  key={item.id}
                  className={`flex items-center gap-4 px-5 py-4 cursor-pointer transition-colors hover:bg-slate-50 ${isChecked ? 'bg-green-50/40' : ''}`}
                  data-testid={`training-item-${idx}`}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => toggleItem(item.id)}
                    disabled={myProgress?.completed}
                    className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500 w-5 h-5 rounded-md"
                  />
                  <span className={`flex-1 text-sm ${isChecked ? 'line-through text-slate-400' : 'text-slate-700'}`}>{item.text}</span>
                  {isChecked && <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />}
                </label>
              );
            })}
          </CardContent>
        </Card>

        {!myProgress?.completed && (
          <div className="flex gap-3 sticky bottom-20 lg:bottom-4 z-10">
            <Button onClick={() => saveProgress(false)} disabled={saving} variant="outline" className="flex-1 h-12 rounded-xl text-sm font-medium btn-press" data-testid="save-progress-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Progress
            </Button>
            <Button onClick={() => saveProgress(true)} disabled={saving || !allDone} className="flex-1 h-12 rounded-xl text-sm font-medium bg-green-500 hover:bg-green-600 text-white btn-press" data-testid="complete-checklist-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Complete
            </Button>
          </div>
        )}
      </div>
    );
  }

  // === Admin progress view ===
  if (viewMode === 'admin-progress' && selectedChecklist) {
    const items = selectedChecklist.items || [];
    return (
      <div className="space-y-5 animate-fade-in" data-testid="training-admin-progress">
        <button onClick={goBack} className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-600 transition-colors"><ArrowLeft className="w-4 h-4" /> Back</button>
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{selectedChecklist.title} — Progress</h2>
        {allProgress.length === 0 ? (
          <Card className="border border-slate-200/80 rounded-2xl"><CardContent className="py-14 text-center">
            <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500 text-sm">No users have started this yet</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-2.5 stagger">
            {allProgress.map(p => {
              const pct = items.length > 0 ? Math.round(((p.checked_items?.length || 0) / items.length) * 100) : 0;
              return (
                <Card key={p.user_id} className="border border-slate-200/80 rounded-xl">
                  <CardContent className="p-4 flex items-center gap-3.5">
                    <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-sm font-bold shrink-0">{(p.user_name || '?').charAt(0)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium text-slate-800 truncate">{p.user_name}</span>
                        {p.completed ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Done</span>
                          : <span className="text-[11px] text-slate-400 tabular-nums">{pct}%</span>}
                      </div>
                      <Progress value={pct} className="h-1.5" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // === Main list ===
  return (
    <div className="space-y-6" data-testid="training-tracker-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Training</h1>
          <p className="text-sm text-slate-500 mt-0.5">Complete your training checklists</p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} size="sm" className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl h-9 px-4 text-[13px] font-medium btn-press" data-testid="create-training-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-slate-300" /></div>
      ) : checklists.length === 0 ? (
        <Card className="border border-slate-200/80 rounded-2xl"><CardContent className="py-16 text-center">
          <GraduationCap className="w-12 h-12 mx-auto mb-3 text-slate-200" />
          <p className="text-slate-500 text-sm">No training checklists yet</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3 stagger">
          {checklists.map(cl => (
            <Card key={cl.id} className="card-interactive border border-slate-200/80 bg-white rounded-2xl" data-testid={`training-cl-${cl.id}`}>
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-500 shrink-0">
                    <GraduationCap className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openChecklist(cl)}>
                    <h3 className="font-semibold text-slate-900 text-[15px]">{cl.title}</h3>
                    <p className="text-[13px] text-slate-400 mt-0.5">{cl.items?.length || 0} items</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/training-quizzes?checklist=${cl.id}`)}
                      className="rounded-xl h-8 px-2.5 text-xs font-medium text-purple-600 border-purple-200 hover:bg-purple-50 hidden sm:inline-flex"
                      data-testid={`quiz-cl-${cl.id}`}
                      title="Take quiz"
                    >
                      <Brain className="w-3.5 h-3.5 mr-1" /> Quiz
                    </Button>
                    {isAdmin && (
                      <>
                        <button onClick={() => viewAdminProgress(cl)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-indigo-400 hover:text-indigo-600 transition-colors" data-testid={`view-progress-${cl.id}`}><Users className="w-4 h-4" /></button>
                        <button onClick={() => openEdit(cl)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 transition-colors"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(cl.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </>
                    )}
                    <Button size="sm" onClick={() => openChecklist(cl)} className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl h-8 px-3 text-xs font-medium btn-press" data-testid={`start-cl-${cl.id}`}>
                      Start <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg">{editingId ? 'Edit' : 'Create'} Training Checklist</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5"><Label className="text-[13px]">Title</Label><Input value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} placeholder="e.g., New Employee Onboarding" className="rounded-xl h-10" data-testid="edit-title-input" /></div>
            <div className="space-y-2">
              <Label className="text-[13px]">Items</Label>
              {editForm.items.map((item, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input value={item} onChange={e => updateItem(idx, e.target.value)} placeholder={`Item ${idx + 1}`} className="rounded-xl h-10" />
                  {editForm.items.length > 1 && <button onClick={() => removeItem(idx)} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors shrink-0"><X className="w-4 h-4" /></button>}
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addItem} className="w-full rounded-xl h-9 text-[13px]"><Plus className="w-3.5 h-3.5 mr-1" /> Add Item</Button>
            </div>
            <Button onClick={handleSaveChecklist} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl h-11 text-sm font-medium btn-press" data-testid="save-checklist-btn">{editingId ? 'Update' : 'Create'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
